import { axios } from "utils";

import { ADMIN_VERIFICATIONS_URL as endpoint } from "consts";

import {
  AddEditingRequestRequestBody,
  AddOrUpdateVerificationTask,
  Coordinates,
  GetFarmVerificationsResponse,
  GetFieldVerificationsQuery,
  GetFieldVerificationsResponseBody,
  GetVerificationEditingRequestsResponse,
  RecalculationTrigger,
  UpdateEditingRequestRequestBodyAdmin,
} from "models";

import { ElementVerificationUpdate, FieldVerificationUpdate } from "pages/admin/verifications/CsvUpload";

// this file should be broken up into multiple files, probably in a verifications directory

// farms

export const getFarmVerifications = async (data: {
  filter?: string;
  assigneeId?: string;
  search: string;
  page: number;
  itemsPerPage: number;
  includeAll?: boolean;
}): Promise<GetFarmVerificationsResponse> => axios.get(`${endpoint}/farms`, { data });

export const getFarmVerificationSummary = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.get(`${endpoint}/farms/${data.farmId}/summary`, { data, signal: data.signal });

export const updateFarmReviewer = async (data: { farmId: string; reviewerId: string; signal: AbortSignal }) =>
  axios.put(`${endpoint}/farms/${data.farmId}/reviewer`, { data: { reviewerId: data.reviewerId }, signal: data.signal });

export const verifyFarm = async (data: { id: string; signal: AbortSignal }) => axios.put(`${endpoint}/farms/${data.id}`, { data, signal: data.signal });

// field

export const verifyField = async (data: { verified: boolean | null; verificationId: string }): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/fields/${data.verificationId}`, { data });

export const updateFieldVerificationSize = async (data: { size: number; verificationId: string }): Promise<void> =>
  axios.put(`${endpoint}/fields/${data.verificationId}/size`, { data });

export const updateFieldVerificationOfficialSize = async (data: {
  officialSize: number | null;
  verificationId: string;
}): Promise<{ officialSize: number | null }> => axios.put(`${endpoint}/fields/${data.verificationId}/official-size`, { data });

// Batch update (used for csv upload for example)

export const verifyFields = async (data: FieldVerificationUpdate[]) => axios.put(`${endpoint}/fields`, { data });

export const getFieldVerifications = async (data: GetFieldVerificationsQuery): Promise<GetFieldVerificationsResponseBody> =>
  axios.get(`${endpoint}/farms/${data.farmId}/fields`, { data });

export const getFieldVerificationSummary = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.get(`${endpoint}/farms/${data.farmId}/fields/summary`, { data, signal: data.signal });

export const getFieldVerificationShape = async (data: {
  verificationId: string;
  signal?: AbortSignal;
}): Promise<{ fieldShape: { geometry: Coordinates[]; center: Coordinates } }> =>
  axios.get(`${endpoint}/fields/${data.verificationId}/shape`, { data, signal: data.signal });

// module

export const verifyFieldModuleElement = async (data: { verificationId: string; signal: AbortSignal }) =>
  axios.put(`${endpoint}/field-module-elements/${data.verificationId}`, { data, signal: data.signal });

// Batch update (used for csv upload for example)

export const verifyFieldModuleElements = async (data: ElementVerificationUpdate[]) => axios.put(`${endpoint}/field-module-elements`, { data });

export const getFieldModuleVerificationSummary = async (data: { farmId: string; moduleId: string; signal: AbortSignal }) =>
  axios.get(`${endpoint}/farms/${data.farmId}/modules/${data.moduleId}/field-modules/summary`, { data, signal: data.signal });

export const getFieldModuleElementVerifications = async (data: { farmId: string; moduleId: string; year: number; signal: AbortSignal }) =>
  axios.get(`${endpoint}/farms/${data.farmId}/modules/${data.moduleId}/field-module-elements/${data.year}`, { data, signal: data.signal });

// comments

export const addVerificationComment = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/comments`, { data, signal: data.signal });

export const getVerificationComments = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/comments`, { data, signal: data.signal });

// tasks

export const getAllVerificationTasks = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/tasks`, { data, signal: data.signal });

export const addOrUpdateVerificationTask = async (data: AddOrUpdateVerificationTask) => axios.post(`${endpoint}/tasks`, { data });

export const removeVerificationTask = async (data: { id: string; signal: AbortSignal }) =>
  axios.delete(`${endpoint}/tasks/${data.id}`, { data, signal: data.signal });

export const addVerificationFarmTask = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.post(`${endpoint}/farms/${data.farmId}/tasks`, { data, signal: data.signal });

export const updateUserVerificationFarmTaskAdmin = async (data: { id: string; signal: AbortSignal }) =>
  axios.put(`${endpoint}/farms/tasks/${data.id}`, { data, signal: data.signal });

export const getAllFarmVerificationTasks = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.get(`${endpoint}/farms/${data.farmId}/tasks`, { data, signal: data.signal });

// payouts

export const getAdminFarmPayoutApprovals = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.get(`${endpoint}/farms/${data.farmId}/payout-approvals`, { data, signal: data.signal });

export const approvePayout = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.post(`${endpoint}/farms/${data.farmId}/payout-approvals/approve`, { data, signal: data.signal });

export const rejectPayout = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.post(`${endpoint}/farms/${data.farmId}/payout-approvals/reject`, { data, signal: data.signal });

// editing requests

export const addEditingRequest = async (data: AddEditingRequestRequestBody) => axios.post(`${endpoint}/editing-requests`, { data });

export const updateEditingRequestAdmin = async (data: UpdateEditingRequestRequestBodyAdmin) => axios.put(`${endpoint}/editing-requests/${data.id}`, { data });

export const getVerificationEditingRequestsAdmin = async (data: { farmId: string }): Promise<GetVerificationEditingRequestsResponse> =>
  axios.get(`${endpoint}/farms/${data.farmId}/editing-requests`, { data });

// crop verification status

export const updateVerificationStatus = async (data: { practiceType: string; practiceId: string; verificationStatus: string }): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/${data.practiceType}/${data.practiceId}/verification-status`, { data });

// Recalculation triggers

export const addRecalculationTrigger = async (data: Pick<RecalculationTrigger, "title" | "description">): Promise<RecalculationTrigger> =>
  axios.post(`${endpoint}/recalculation-triggers`, { data });

export const getRecalculationTrigger = async (data: { id: string }): Promise<RecalculationTrigger> =>
  axios.get(`${endpoint}/recalculation-triggers/${data.id}`);

export const getLatestRecalculationTrigger = async (): Promise<RecalculationTrigger | null> => axios.get(`${endpoint}/recalculation-triggers/latest`);
